/**
 * Weighted evidence model for rising-sign rectification.
 *
 * The single biggest failure mode of an appearance-aware rectifier is OVER-RELYING on sign-only
 * physical stereotypes: a validated chart (e.g. Johnny Depp — Cancer lagna, but lean/angular/
 * long-nosed, not the round/stocky Cancer cliché) must NOT be rejected just because the sign
 * checklist fails. The fix is structural: dated life events are PRIMARY, and appearance is a
 * small, bounded modifier that can never decide — or veto — a birth time.
 *
 * Default evidence weights (owner spec):
 *   - Dated life events ........ 50%   (the only second-resolution signal; the time authority)
 *   - Personality / life pattern 20%   (dasha-period life themes — constant across a sign window)
 *   - Appearance ............... 15%   (modifier-based physical match — NEVER sign-only, NEVER decisive)
 *   - Family / body background . 10%   (reserved bucket; the facts feature that fed it has been
 *                                       removed, so it is now always absent and drops out cleanly)
 *   - Archetype / gut match .... 5%    (holistic resemblance estimate)
 *
 * `combine_evidence` is a weighted average over the buckets that are PRESENT (a null bucket is
 * dropped and the remaining weights renormalise). Two consequences are load-bearing:
 *   1. A bucket's maximum influence is capped at its (renormalised) weight — so a weak or even
 *      zero appearance can only pull a strong-events chart down by a bounded amount; it can never
 *      zero it out.
 *   2. Because events carry the largest weight, the combined score tracks the events far more than
 *      appearance — exactly the priority the spec asks for.
 *
 * Pure and side-effect free; unit-tested in `evidence_weights.test.ts`.
 */

export type EvidenceBucket = "events" | "personality" | "appearance" | "family" | "archetype";

/** Owner-spec default weights. Sum to 1.0. */
export const EVIDENCE_WEIGHTS: Record<EvidenceBucket, number> = {
  events: 0.5,
  personality: 0.2,
  appearance: 0.15,
  family: 0.1,
  archetype: 0.05,
};

/** Below this appearance score the sign-only match is treated as "weak" → contradiction note. */
export const APPEARANCE_WEAK_BELOW = 55.0;
/** Appearance is flagged as conflicting with the events when it trails them by at least this much. */
export const APPEARANCE_CONFLICT_GAP = 12.0;

function _clamp(x: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, x));
}

/**
 * Renormalise a weight map so the (non-negative) weights sum to 1. An all-zero/empty map yields
 * equal weights. Negative weights are floored at 0.
 */
export function normalize_weights(weights: Record<string, number>): Record<string, number> {
  const keys = Object.keys(weights);
  if (keys.length === 0) return {};
  const clean = keys.map((k) => [k, Math.max(0, Number(weights[k]) || 0)] as [string, number]);
  const sum = clean.reduce((a, [, v]) => a + v, 0);
  if (sum <= 0) {
    const eq = 1 / keys.length;
    return Object.fromEntries(keys.map((k) => [k, eq]));
  }
  return Object.fromEntries(clean.map(([k, v]) => [k, v / sum]));
}

export interface CombineResult {
  /** The weighted-average score over present buckets, clamped 0–100; null if no bucket present. */
  score: number | null;
  /** The renormalised weights actually used (over the present buckets only). */
  used_weights: Record<string, number>;
  /** Each present bucket's contribution to the score (used_weight × value). */
  contributions: Record<string, number>;
  /** Bucket names that contributed. */
  present: string[];
  /** Bucket names that were null/absent and therefore dropped. */
  missing: string[];
}

/**
 * Weighted average over the PRESENT (non-null, non-NaN) buckets, with the weights renormalised
 * across those present. A missing bucket simply doesn't participate — it neither helps nor hurts.
 * Returns score=null only when every bucket is absent.
 */
export function combine_evidence(
  buckets: Partial<Record<EvidenceBucket, number | null | undefined>>,
  weights: Record<string, number> = EVIDENCE_WEIGHTS,
): CombineResult {
  const order = Object.keys(weights);
  const present = order.filter((b) => {
    const v = buckets[b as EvidenceBucket];
    return v !== null && v !== undefined && !Number.isNaN(Number(v));
  });
  const missing = order.filter((b) => !present.includes(b));
  if (present.length === 0) {
    return { score: null, used_weights: {}, contributions: {}, present: [], missing };
  }
  const sub = normalize_weights(Object.fromEntries(present.map((b) => [b, weights[b]])));
  const contributions: Record<string, number> = {};
  let score = 0;
  for (const b of present) {
    const c = sub[b] * Number(buckets[b as EvidenceBucket]);
    contributions[b] = c;
    score += c;
  }
  return { score: _clamp(score, 0, 100), used_weights: sub, contributions, present, missing };
}

/**
 * Honest contradiction text for the report. When the appearance bucket is present but weak — and
 * especially when it trails the dated events — explain that this does NOT invalidate the birth
 * time. Returns null when appearance is absent or is not in conflict.
 *
 * `signName` and `signOnlyWeak` come from the appearance scorer (sign-only baseline weak), so the
 * note can speak to the specific lagna and point at the modifiers that should be checked instead.
 */
export function appearance_conflict_note(args: {
  appearance: number | null | undefined;
  events: number | null | undefined;
  signName?: string | null;
  signOnlyWeak?: boolean;
}): string | null {
  const { appearance, events, signName, signOnlyWeak } = args;
  if (appearance === null || appearance === undefined || Number.isNaN(Number(appearance))) return null;
  const weak = signOnlyWeak || appearance < APPEARANCE_WEAK_BELOW;
  const trailsEvents = events !== null && events !== undefined && !Number.isNaN(Number(events)) && events - appearance >= APPEARANCE_CONFLICT_GAP;
  if (!weak && !trailsEvents) return null;
  const sign = signName ? `${signName} lagna ` : "";
  return (
    `${sign}sign-only appearance match is weak (${Math.round(Number(appearance))}%), but this does NOT ` +
    `invalidate the birth time. Appearance is secondary evidence (15% weight) — never decisive. The ` +
    `dated life events (primary, 50%) and the appearance MODIFIERS (lagna nakshatra & its lord, the ` +
    `lagna lord's condition, planets in/aspecting the 1st house, age) carry the rectification. Check ` +
    `the event timing and those modifiers before rejecting this lagna on looks alone.`
  );
}
